/**
 * Compression utilities for session snapshots
 * Uses browser-native CompressionStream/DecompressionStream APIs
 */

/**
 * Check if compression APIs are available in the browser
 */
function isCompressionSupported(): boolean {
  return typeof CompressionStream !== 'undefined' && typeof DecompressionStream !== 'undefined';
}

/**
 * Compress a string using gzip compression
 * @param data - String data to compress
 * @returns Base64-encoded compressed string
 * @throws Error if compression APIs are not available
 */
export async function compressSnapshot(data: string): Promise<string> {
  if (!isCompressionSupported()) {
    throw new Error('CompressionStream API not available in this browser');
  }
  const encoder = new TextEncoder();
  const stream = new CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();

  // Write data to compression stream
  await writer.write(encoder.encode(data));
  await writer.close();

  // Read compressed chunks
  const chunks: Uint8Array[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
    }
  }

  // Combine chunks and convert to base64
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  // Convert to base64 using binary string approach
  // Build binary string from bytes (bytes are 0-255, safe for charCodeAt)
  let binary = '';
  for (const element of combined) {
    binary += String.fromCodePoint(element ?? 0);
  }
  return btoa(binary);
}

/**
 * Decompress a gzip-compressed string
 * @param compressed - Base64-encoded compressed string
 * @returns Decompressed string
 * @throws Error if decompression APIs are not available
 */
export async function decompressSnapshot(compressed: string): Promise<string> {
  if (!isCompressionSupported()) {
    throw new Error('DecompressionStream API not available in this browser');
  }
  // Decode from base64
  const binary = atob(compressed);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    // For base64 decoded strings, each character represents a byte (0-255)
    // Use codePointAt (base64 strings are single-byte, so no surrogate pairs)
    const codePoint = binary.codePointAt(i);
    bytes[i] = codePoint ?? 0;
  }

  // Decompress using DecompressionStream
  const stream = new DecompressionStream('gzip');
  const writer = stream.writable.getWriter();
  const reader = stream.readable.getReader();

  // Write compressed data to decompression stream
  await writer.write(bytes);
  await writer.close();

  // Read decompressed chunks
  const chunks: Uint8Array[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
    }
  }

  // Combine chunks and convert to string
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  const decoder = new TextDecoder();
  return decoder.decode(combined);
}
